<template>
  <div>
    <page-header></page-header>

    <div class="container page-content">
      <page-steps :steps="pageSteps"></page-steps>

      <form class="form-horizontal">
        <div class="row">
          <div class="col-md-8 _margin-top-20">
            <div class="alert alert-info alert-energy-update">
              <template v-if="app.showGovernmentEnergyUpdate">
                <p class="alert-energy-update__headline">
                  <strong>
                    Following measures announced by the UK Government in the 2025 Budget, energy costs are set to decrease from Wednesday 1 April 2026.
                  </strong>
                </p>
                <p>
                  From that date, certain taxes and government charges will be removed from energy bills, which is expected to reduce costs by around £150 per year for the average household. Energy suppliers will reflect these changes in both new and existing tariffs.
                </p>
                <p>
                  As a result, quotes provided before 1 April 2026 may vary depending on how individual suppliers implement the updated rates.
                </p>
                <p>
                  When comparing tariffs, please take into account both your current rates and any changes that will apply from 1 April 2026.
                </p>
              </template>
              <p>
                Please ensure you have a copy of your latest energy bill to hand
              </p>
            </div>

            <your-home :$v="$v.form" v-show="showStep('address')"></your-home>
            <compare :$v="$v.form" v-show="showStep('compareFuel')"></compare>
            <economy7 :$v="$v.form" v-show="showStep('isEconomy7')"></economy7>
            <payment-type :$v="$v.form"
              v-show="showStep('currentPayMethod') && isPrepaymentBoth == false"></payment-type>
            <supplier-tariff :$v="$v.form" v-show="showStep('supplierId.tariff.tariffValidToDate')"></supplier-tariff>
            <usage :$v="$v.form" v-show="showStep('gasUsage.electricityUsage')"></usage>
            <payment-type-gas :$v="$v.form"
              v-show="showStep('gasCurrentPayMethod') && isPrepaymentGas == false"></payment-type-gas>
            <supplier-tariff-gas :$v="$v.form"
              v-show="showStep('gasSupplierId.gasTariff.gasTariffValidToDate')"></supplier-tariff-gas>
            <usage-gas :$v="$v.form.gasUsage" v-show="showStep('gasUsage')"></usage-gas>
            <payment-type-electricity :$v="$v.form"
              v-show="showStep('electricityCurrentPayMethod') && isPrepaymentElectricity == false"></payment-type-electricity>
            <supplier-tariff-electricity :$v="$v.form"
              v-show="showStep('electricitySupplierId.electricityTariff.electricityTariffValidToDate')"></supplier-tariff-electricity>
            <usage-electricity :$v="$v.form.electricityUsage" v-show="showStep('electricityUsage')"></usage-electricity>
            <economy-7-percentage :$v="$v.form" v-show="showStep('economy7Percentage')"></economy-7-percentage>

            <uiv-alert type="danger" v-if="$v.form.$error">
              <b>Error</b> <br />
              Please ensure you answered all required fields
            </uiv-alert>

            <uiv-alert type="danger" v-if="error">
              <b>Error</b> <br />
              {{ error }}
            </uiv-alert>

            <div class="text-right" v-if="$v.form.$invalid == false">
              <uiv-btn type="primary" @click="submit" :disabled="loading">
                <loader :show="loading" />
                View Your Quotes
              </uiv-btn>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import YourHome from '@/components/current-tariff/YourHome';
import Compare from '@/components/current-tariff/Compare';
import SameSupplier from '@/components/current-tariff/SameSupplier';
import Economy7 from '@/components/current-tariff/Economy7';
import PaymentType from '@/components/current-tariff/PaymentType';
import PaymentTypeGas from '@/components/current-tariff/PaymentTypeGas';
import PaymentTypeElectricity from '@/components/current-tariff/PaymentTypeElectricity';

import SupplierTariff from '@/components/current-tariff/SupplierTariff';
import SupplierTariffElectricity from '@/components/current-tariff/SupplierTariffElectricity';
import SupplierTariffGas from '@/components/current-tariff/SupplierTariffGas';

import Usage from '@/components/current-tariff/Usage';
import UsageGas from '@/components/current-tariff/UsageGas';
import UsageElectricity from '@/components/current-tariff/UsageElectricity';
import Economy7Percentage from '@/components/current-tariff/Economy7Percentage';
import PaymentTypeNew from '@/components/current-tariff/PaymentTypeNew';

import PageHeader from '@/components/page-header/PageHeader';
import PageSteps from '@/components/page-steps/PageSteps';

import { mapState, mapGetters } from 'vuex';

import {
  required, helpers, requiredIf,
  maxLength, numeric,
  minValue, maxValue
} from 'vuelidate/lib/validators';

export default {
  name: 'CurrentTariff',
  data() {
    return {
      pageSteps: [
        { name: 'Your Current Energy Information', active: true },
        { name: 'Choose Your New Deal' },
        { name: 'Sign Up' },
        { name: 'Switch Complete' }
      ],
      form: {
        vehicle: {},
        compareVehicle: {},
        vehicleUsage: {},
        address: {},
        gasUsage: {},
        electricityUsage: {},
        tariff: {},
        gasTariff: {},
        electricityTariff: {}
      },
      costConditions: [null, { min: 120, max: 9000 }, { min: 10, max: 750 }, { min: 30, max: 2250 }],
      electricityUsageConditions: [null, { min: 1, max: 75000 }, { min: 1, max: 6250 }, { min: 1, max: 18750 }],
      gasUsageConditions: [null, { min: 1, max: 200000 }, { min: 1, max: 16600 }, { min: 1, max: 50000 }],
      error: null
    };
  },
  mounted() {
    this.$store.commit('quote/resetState');
    this.$store.commit('lookups/resetState');
    this.$store.commit('storage/reset');
    this.handleUrlReferences();
    this.form = this.quote;
  },
  validations() {
    return {
      form: {
        postcode: {
          required,
          maxLength: maxLength(7),
          regex: helpers.regex('postcode', /^(([gG][iI][rR] {0,}0[aA]{2})|((([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]?[0-9][0-9]?)|(([a-pr-uwyzA-PR-UWYZ][0-9][a-hjkstuwA-HJKSTUW])|([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y][0-9][abehmnprv-yABEHMNPRV-Y]))) {0,}[0-9][abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2}))$/)
        },
        compareFuel: {
          required,
          prepaymentMismatch: (value, form) => {
            if (!form.address.parts || form.compareFuel !== 'gasElectricity') return true;
            let { gasdetails, elecdetails } = form.address.parts;
            gasdetails = gasdetails.find(({ isDomestic }) => isDomestic) || {};
            elecdetails = elecdetails.find(({ isDomestic }) => isDomestic) || {};

            const types = [gasdetails.isPrepayment, elecdetails.isPrepayment];

            if (types.indexOf(true) > -1) {
              return gasdetails.isPrepayment == elecdetails.isPrepayment;
            }

            return true;
          }
        },
        address: {
          required,
          domesticMeter: (value, form) => {
            if (!value.parts) return false;
            const address = value.parts;
            const gasdetails = address.gasdetails.find(({ isDomestic }) => isDomestic) || {};
            const elecdetails = address.elecdetails.find(({ isDomestic }) => isDomestic) || {};

            if (form.compareFuel == 'gas') {
              return true;
            }

            if (form.compareFuel == 'gasElectricity' && gasdetails.isDomestic && !elecdetails.isDomestic) {
              return true;
            }

            return elecdetails?.isDomestic ? true : false;;
          }
        },
        sameSupplier: {
          requiredIf: requiredIf(form => form.compareFuel === 'gasElectricity')
        },
        isEconomy7: {
          requiredIf: requiredIf(form => this.is02Meter)
        },
        supplierId: {
          requiredIf: requiredIf(form => this.quoteType === 'dualConsolidatedToDualConsolidated')
        },
        currentPayMethod: {
          requiredIf: requiredIf(form => this.quoteType === 'dualConsolidatedToDualConsolidated')
        },
        gasCurrentPayMethod: {
          requiredIf: requiredIf(form => {
            const { quoteType } = this;

            return quoteType === 'gasToGas' || quoteType === 'dualDistinctToDualConsolidated';
          }),
          mustBePrepayment: (value, form) => {
            const { quoteType } = this;
            if (!value || quoteType === 'gasToGas' || quoteType === 'dualConsolidatedToDualConsolidated') return true;

            if (form.electricityCurrentPayMethod === 'prePaymentMeter') {
              return value === 'prePaymentMeter';
            } else {
              return true;
            }
          }
        },
        electricityCurrentPayMethod: {
          requiredIf: requiredIf(form => {
            const { quoteType } = this;

            return quoteType === 'electricityToElectricity' || quoteType === 'dualDistinctToDualConsolidated';
          }),
          mustBePrepayment: (value, form) => {
            const { quoteType } = this;
            if (!value || quoteType === 'electricityToElectricity' || quoteType === 'dualConsolidatedToDualConsolidated') return true;

            if (form.gasCurrentPayMethod === 'prePaymentMeter') {
              return value === 'prePaymentMeter';
            } else {
              return true;
            }
          }
        },
        tariff: {
          id: {
            requiredIf: requiredIf(form => this.quoteType === 'dualConsolidatedToDualConsolidated')
          }
        },
        tariffValidToDate: {
          requiredIf: requiredIf(form => form.tariff.isFixedFromEnergyStartDate)
        },
        gasSupplierId: {
          requiredIf: requiredIf(form => {
            const { quoteType } = this;

            return quoteType === 'gasToGas' || quoteType === 'dualDistinctToDualConsolidated';
          })
        },
        electricitySupplierId: {
          requiredIf: requiredIf(form => {
            const { quoteType } = this;

            return quoteType === 'electricityToElectricity' || quoteType === 'dualDistinctToDualConsolidated';
          })
        },
        gasTariff: {
          id: {
            requiredIf: requiredIf(form => {
              const { quoteType } = this;

              return quoteType === 'gasToGas' || quoteType === 'dualDistinctToDualConsolidated';
            })
          }
        },
        gasTariffValidToDate: {
          requiredIf: requiredIf(form => form.gasTariff.isFixedFromEnergyStartDate)
        },
        electricityTariff: {
          id: {
            requiredIf: requiredIf(form => {
              const { quoteType } = this;

              return quoteType === 'electricityToElectricity' || quoteType === 'dualDistinctToDualConsolidated';
            })
          }
        },
        electricityTariffValidToDate: {
          requiredIf: requiredIf(form => form.electricityTariff.isFixedFromEnergyStartDate)
        },
        gasUsage: {
          usage: {
            requiredIf: requiredIf(() => {
              const { quoteType, form } = this;

              if (quoteType !== 'electricityToElectricity') {
                return form.gasUsage.usageType == 1;
              }

              return false;
            }),
            numeric,
            minValue: minValue(this.gasUsageConditions[this.quote.gasUsage.usageUnit].min),
            maxValue: maxValue(this.gasUsageConditions[this.quote.gasUsage.usageUnit].max)
          },
          cost: {
            requiredIf: requiredIf(() => {
              const { quoteType, form } = this;

              if (quoteType !== 'electricityToElectricity') {
                return form.gasUsage.usageType == 0;
              }

              return false;
            }),
            numeric,
            minValue: minValue(this.costConditions[this.quote.gasUsage.usageUnit].min),
            maxValue: maxValue(this.costConditions[this.quote.gasUsage.usageUnit].max)
          }
        },
        electricityUsage: {
          usage: {
            requiredIf: requiredIf(() => {
              const { quoteType, form } = this;

              if (quoteType !== 'gasToGas') {
                return form.electricityUsage.usageType == 1;
              }

              return false;
            }),
            numeric,
            minValue: minValue(this.electricityUsageConditions[this.quote.electricityUsage.usageUnit].min),
            maxValue: maxValue(this.electricityUsageConditions[this.quote.electricityUsage.usageUnit].max)
          },
          cost: {
            requiredIf: requiredIf(() => {
              const { quoteType, form } = this;

              if (quoteType !== 'gasToGas') {
                return form.electricityUsage.usageType == 0;
              }

              return false;
            }),
            numeric,
            minValue: minValue(this.costConditions[this.quote.electricityUsage.usageUnit].min),
            maxValue: maxValue(this.costConditions[this.quote.electricityUsage.usageUnit].max)
          }
        },
        economy7Percentage: {
          minValue: minValue(1),
          maxValue: maxValue(100)
        },
        newTariffPaymentMethod: {
          requiredIf: requiredIf(form => {
            const { quoteType } = this;
            const { currentPayMethod, gasCurrentPayMethod, electricityCurrentPayMethod, newTariffPaymentMethod } = this.form;

            if (quoteType == 'dualConsolidatedToDualConsolidated') {
              return currentPayMethod !== 'prePaymentMeter';
            }

            if (quoteType == 'gasToGas') {
              return gasCurrentPayMethod !== 'prePaymentMeter';
            }

            if (quoteType == 'electricityToElectricity') {
              return electricityCurrentPayMethod !== 'prePaymentMeter';
            }

            return gasCurrentPayMethod !== 'prePaymentMeter' && electricityCurrentPayMethod !== 'prePaymentMeter';
          })
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      journey: 'quote_questions/journey',
      quoteType: 'quote/quoteType',
      is02Meter: 'quote/is02Meter',
      noMpan: 'quote/noMpan',
      nonDomesticMeter: 'quote/nonDomesticMeter'
    }),
    ...mapState({
      quote: ({ quote }) => quote,
      app: ({ app_settings }) => app_settings,
      loading: ({ page_loader }) => page_loader.show,
      fuelTypeList: ({ lookups }) => lookups.fuelTypes,
      supplierList: ({ lookups }) => lookups.suppliers,
      supplierGasList: ({ lookups }) => lookups.suppliersGas,
      supplierElectricityList: ({ lookups }) => lookups.suppliersElectricity,
      paymentMethodList: ({ lookups }) => lookups.paymentMethods,
      usageUnitsList: ({ lookups }) => lookups.usageUnits,
      isPrepaymentBoth: ({ quote }) => {
        const address = quote.address.parts;
        if (!address) return false;

        let { gasdetails, elecdetails } = address;
        gasdetails = gasdetails.find(({ isDomestic }) => isDomestic) || {};
        elecdetails = elecdetails.find(({ isDomestic }) => isDomestic) || {};

        if (quote.compareFuel == 'electricity') {
          return elecdetails.isPrepayment == true;
        }

        if (quote.compareFuel == 'gas') {
          return gasdetails.isPrepayment == true;
        }

        return elecdetails.isPrepayment == true && gasdetails.isPrepayment == true;
      },
      isPrepaymentElectricity: ({ quote }) => {
        const address = quote.address.parts;
        if (!address) return false;
        let { elecdetails } = address;
        elecdetails = elecdetails.find(({ isDomestic }) => isDomestic) || {};

        return elecdetails.isPrepayment == true;
      },
      isPrepaymentGas: ({ quote }) => {
        const address = quote.address.parts;
        if (!address) return false;
        let { gasdetails } = address;
        gasdetails = gasdetails.find(({ isDomestic }) => isDomestic) || {};

        return gasdetails.isPrepayment == true;
      }
    })
  },
  methods: {
    showStep(step) {
      const form = this.$v.form;
      const index = this.journey.indexOf(step);

      if (index < 0) { return false; }
      if (index == 0) { return true; }

      const validSteps = this.journey.map((key) => {
        const split = key.split('.');

        if (split.length > 1) {
          const invalids = split.map(key => this.$v.form[key].$invalid);
          return invalids.indexOf(true) < 0;
        }

        return !this.$v.form[key].$invalid;
      });

      const previousSteps = validSteps.slice(0, index);
      const previousValidSteps = previousSteps.filter((v) => v);

      return previousValidSteps.length == index;
    },
    handleUrlReferences() {
      // if url has a refer params store and use it as the refer otherwise use .env refer branch
      if (this.$route.params.reference && this.$route.params.refererBranch) {
        this.$store.dispatch('app_settings/storeReferences', this.$route.params);
        this.$store.dispatch('app_settings/setReferences');
      }
    },
    async submit() {
      this.error = null;
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.$store.dispatch('tracing/addAction', { name: 'Page', value: 'Create Quote' });
      this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Submit' });
      await this.$store.dispatch('tracing/saveActions');
      const error = await this.$store.dispatch('quote/createQuote');

      if (error) {
        return this.error = error;
      }

      this.$router.push({
        name: 'quoteResults'
      });
    }
  },
  components: {
    YourHome,
    Compare,
    SameSupplier,
    Economy7,
    PaymentType,
    PaymentTypeGas,
    PaymentTypeElectricity,
    SupplierTariff,
    SupplierTariffElectricity,
    SupplierTariffGas,
    Usage,
    UsageGas,
    UsageElectricity,
    Economy7Percentage,
    PaymentTypeNew,

    PageHeader,
    PageSteps
  }
};
</script>

<style lang="scss" scoped>
.page-content {
  padding-bottom: 160px;
}

.alert-energy-update {
  p {
    margin-bottom: 8px;
  }

  &__headline {
    font-weight: 700;
    font-size: 16px;
  }
}

.card {
  width: 100%;
  padding: 15px 24px 24px 24px;
  background-color: #fff;
  display: inline-block;
  margin-bottom: 20px;
  border-top: 5px solid $brand-colour-primary;

  h6 {
    font-size: 11px;
    color: $font-paragraph-colour;
  }
}
</style>
